/* oxlint eqeqeq: 'off' */
/* oxlint no-param-reassign: 'off' */
/*

The MIT License (MIT)

Copyright (c) 2015 Chris Corbyn.

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.

*/

'use strict'

/*! The address used when not specified */
const DEFAULT_ADDRESS = 0
/*! The prefix length used when not specified */
const DEFAULT_PREFIX = 32
/*! The lowest permissible address */
const MIN_ADDRESS = 0
/*! The highest permissible address */
const MAX_ADDRESS = 0xffffffff

/** Strict syntax check before embedding an IPv4 address in protocol configuration. */
export function isValidIpv4Address(value: unknown): value is string {
    if (typeof value !== 'string') return false
    const octets = value.split('.')
    return octets.length === 4 && octets.every((octet) => /^(?:0|[1-9]\d{0,2})$/u.test(octet) && Number(octet) <= 255)
}

/** Receive bindings must name an address so their whitelists can be isolated by destination. */
export function isValidIpv4ReceiveAddress(value: unknown): value is string {
    return isValidIpv4Address(value) && value !== '0.0.0.0'
}

/** Strict syntax check for an IPv4 CIDR before embedding it in protocol configuration. */
export function isValidIpv4Cidr(value: unknown): value is string {
    if (typeof value !== 'string' || value.length === 0 || value !== value.trim()) return false
    const parts = value.split('/')
    if (parts.length !== 2) return false
    const [address = '', prefixText = ''] = parts
    if (!isValidIpv4Address(address)) return false
    return /^(?:0|[1-9]\d?)$/u.test(prefixText) && Number(prefixText) <= 32
}

/** Normalizes the legacy bare-IPv4 whitelist form to an explicit host CIDR. */
export function normalizeIpv4Cidr(value: unknown): string | undefined {
    if (isValidIpv4Cidr(value)) return value
    if (typeof value !== 'string' || value.length === 0 || value !== value.trim()) return undefined
    return isValidIpv4Address(value) ? `${value}/32` : undefined
}

export function getOverlappingCidrBlocks(cidrBlocks: string[]): { cidr1: Addrv4; cidr2: Addrv4 } | undefined {
    for (let i = 0; i < cidrBlocks.length; ++i) {
        for (let j = 0; j < cidrBlocks.length; ++j) {
            if (i === j) {
                continue
            }
            const cidr1 = cidrBlocks[i]
            const cidr2 = cidrBlocks[j]
            const addr1 = Addrv4.make(cidr1)
            const addr2 = Addrv4.make(cidr2)
            const isOverlappingCidrBlocks = addr1.intersect(addr2) !== undefined
            if (isOverlappingCidrBlocks) {
                return { cidr1: addr1, cidr2: addr2 }
            }
        }
    }
    return undefined
}

export class Addrv4 {
    private intval: number
    private octets: number[]
    private prefix: number

    /**
     * Representation of an IPv4 address or CIDR.
     *
     * For convenience, this constructor may be invoked as a regular function.
     *
     * @constructor
     *
     * @param {Object} address
     *   either a string or an integer
     *
     * @param {Number} prefix, optional
     *   the length of the netmask prefix
     *
     * @throws {Error}
     *   if the address is not valid
     */
    static make(address: string | number, prefix?: number): Addrv4 {
        try {
            switch (typeof address) {
                case 'undefined':
                    return Addrv4.make(DEFAULT_ADDRESS, prefix)
                case 'string':
                    return Addrv4.parseString(address, prefix)
                case 'number':
                    return Addrv4.parseNumber(address, prefix)
                default:
                    throw new Error(`Invalid Addr type: ${typeof address}`)
            }
        } catch (e: any) {
            throw new Error(`Invalid Addr: ${address}: ${e.toString()}`)
        }
    }
    /*! Parse an IPv4 address string with optional prefix */
    static parseString(address: string, prefix?: number): Addrv4 {
        const parts = address.split('/')
        if (parts.length > 2) {
            throw new Error(`Invalid Addr: ${address}`)
        }

        const octets = parts[0].split('.').map((n: string) => Addrv4.parseByte(n, 255))
        const intval = Addrv4.octetsToInt(octets)
        const prefixByte = Addrv4.parseByte(
            typeof prefix === 'undefined' ? parts[1] || DEFAULT_PREFIX.toString() : prefix.toString(),
            32
        )
        return new Addrv4(intval, octets, prefixByte)
    }
    /*! Parse an IPv4 address integer with optional prefix */
    static parseNumber(address: number, prefix?: number): Addrv4 {
        if (address > MAX_ADDRESS || address < MIN_ADDRESS) {
            throw new Error(`Invalid Addrv4 ${address}`)
        }

        const octets = [255, 255, 255, 255]
            .map(function (octet, offset) {
                return (address >>> (offset * 8)) & octet
            })
            .reverse()
        const intval = Addrv4.octetsToInt(octets)
        const prefixByte = Addrv4.parseByte(
            typeof prefix === 'undefined' ? DEFAULT_PREFIX.toString() : prefix.toString(),
            32
        )
        return new Addrv4(intval, octets, prefixByte)
    }

    private constructor(intval: number, octets: number[], prefix: number) {
        this.intval = intval
        this.octets = octets
        this.prefix = prefix
        void this.octets
    }

    /*! Convert the given octets to an integer */
    private static octetsToInt(octets: number[]) {
        return [...octets]
            .reverse()
            .map(function (octet, offset) {
                return octet * (1 << (offset * 8))
            })
            .reduce(function (x, y) {
                return x + y
            })
    }

    /**
     * Perform a parseInt(), only if a valid byte value with an optional max.
     *
     * @param {String} n
     *   the value to parse
     *
     * @param {Number} max
     *   the maximum allowed value
     *
     * @return {Number}
     *   the parsed integer
     */
    private static parseByte(n: string, max: number) {
        max = max || 255

        if (/^[0-9]+$/.test(n)) {
            const m = parseInt(n)
            if (m <= max) {
                return m
            }
        }
        throw new Error(`${n}: must be a decimal value <= ${max}`)
    }

    /**
     * Return the CIDR range that is the intersection of `this` and `other`.
     *
     * Because CIDR subnets cannot possibly partially overlap, in practice this
     * method returns either `this` or `other` where there is a match. The
     * returned Addrv4 will always be the smallest range.
     *
     * @param {Addrv4} other
     *   another Addrv4 to intersect with
     *
     * @return {Addrv4}
     *   the intersection of the two Addrv4, or undefined if not possible
     */
    public intersect(other: Addrv4) {
        return (this.contains(other) && other) || (other.contains(this) && this) || undefined
    }

    /**
     * Return true if this Addrv4 completely contains another Addrv4.
     *
     * @param {Addrv4} other
     *   the other Addrv4 to check
     *
     * @return {Boolean}
     *   true if other is a subset of this, false if not
     */
    private contains(other: Addrv4) {
        return this.onNetwork(other.network()) && this.onNetwork(other.broadcast())
    }

    /*! Return true if a fixed IP is on this network */
    private onNetwork(ip: Addrv4) {
        return (this.netmask().toInt() & ip.toInt()) >>> 0 == this.network().toInt()
    }

    /**
     * Return the broadcast address from this Addr.
     *
     * This equates to the last address in the CIDR range.
     *
     * @return {Number}
     *   the broadcast address
     */
    private broadcast() {
        return Addrv4.make((this.network().toInt() | ~this.netmask().toInt()) >>> 0)
    }

    /**
     * Return the network address from this Addrv4.
     *
     * This equates to the first address in the CIDR range.
     *
     * @return {Addrv4}
     *   the network address
     */
    private network() {
        return Addrv4.make((this.toInt() & this.netmask().toInt()) >>> 0)
    }

    /**
     * Return the integer representation of this Addrv4.
     *
     * @return {Number}
     *   the integer value of the Addrv4
     */
    private toInt() {
        return this.intval
    }

    /**
     * Return the netmask value for this Addrv4.
     *
     * @return {Addrv4}
     *   the netmask
     */
    private netmask() {
        switch (this.prefix) {
            case 0:
                return Addrv4.make(0x00000000)
            case 32:
                return Addrv4.make(0xffffffff)
            default:
                return Addrv4.make(~(0xffffffff >>> this.prefix) >>> 0)
        }
    }

    /**
     * Return the string representation of this Addr.
     *
     * @return {String}
     *   the string representation, including the mask
     */
    public toString() {
        return [this.octets.join('.'), this.prefix].join('/')
    }
}
